import { useEffect, useState, useCallback } from "react";
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, Paper, Typography, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent, Alert, Tooltip, LinearProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import NotFound from "../../../app/errors/NotFound";
import OrderHelpInOrderChat from "./AdminOrderHelpInOrderChat";
import AppBreadcrumbs from "../../../app/components/AppBreadcrumbs";
import AppTimeline, { Align, TimeLineElement } from "../../../app/components/AppTimeLine";
import { adminOrdersSelectors, fetchAdminOrderAsync, fetchAdminOrdersAsync, setAdminOrder } from "./adminOrdersSlice";
import agent from "../../../app/api/agent";
import { toast } from "react-toastify";
import { currencyFormat, formatToLocalDate, formatToLocalTime, getOrderStatusColor, getOrderStatusTitle } from "../../../app/util/util";
import { Order, OrderState } from "../../../app/models/order";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import { fetchClientOrderAsync, setClientOrder } from "../../client/orders/clientOrdersSlice";
import AppReturn from "../../../app/components/AppReturn";
import AdminOrderScanUploadForm from "./AdminOrderScanUploadForm";
import { LoadingButton } from "@mui/lab";
import DownloadAndPreviewButtons from "../../../app/components/AppDownloadAndPreviewButtons";
import { CollapsibleParticipantsList } from "../../../app/components/Order/OrderParticipantsList";
import AdminTestAvailabilityAlerts from "./AdminTestAvailabilityAlerts";
import AdminTestQuestionsEditor from "./AdminTestQuestionsEditor";
import AdminTestFileUploader from "./AdminTestFileUploader";
import TestAvailabilityConfirmDialog from "./TestAvailabilityConfirmDialog";
import ExamApprovalConfirmDialog from "./ExamApprovalConfirmDialog";
import AppExpandableText from "../../../app/components/AppExpandableText";
export default function AdminOrderDetails() {
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const order = useAppSelector(state => adminOrdersSelectors.selectById(state, id!));
    const { status: orderStatus } = useAppSelector(state => state.adminOrders);
    const [loading, setLoading] = useState(false);
    const [isTestAvailable, setIsTestAvailable] = useState<string>("Nie"); // Domyślnie "Nie"
    const [timeLineElements, setTimeLineElements] = useState<TimeLineElement[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRejecting] = useState(false);
    const [open, setOpen] = useState(false);
    const [loadingAvailability, setLoadingAvailability] = useState(false);
    const [loadingGraphical, setLoadingGraphical] = useState(false); // Nowy stan dla ładowania zmiany typu usługi
    const [isGraphical, setIsGraphical] = useState(order?.isGraphical ? "Tak" : "Nie"); // Nowy stan dla typu usługi
    const [isAIGenerating, setIsAIGenerating] = useState(order?.isTestGenerationInProgress || false); // Stan generowania pytań AI
    const [isDownloadingTest, setIsDownloadingTest] = useState(false);
    const [isDownloadingKey, setIsDownloadingKey] = useState(false);
    const [isDownloadingGraphicalTest, setIsDownloadingGraphicalTest] = useState(false);
    const [isDownloadingGraphicalKey, setIsDownloadingGraphicalKey] = useState(false);
    const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
    const [pendingGraphicalValue, setPendingGraphicalValue] = useState<string | null>(null); // Stan do przechowania wybranej wartości przed potwierdzeniem
    const [openGraphicalDialog, setOpenGraphicalDialog] = useState(false); // Stan okna dialogowego
    const [openTestAvailabilityDialog, setOpenTestAvailabilityDialog] = useState(false); // Dialog potwierdzenia udostępnienia testu
    const [pendingTestAvailability, setPendingTestAvailability] = useState<boolean>(false); // Oczekująca wartość udostępnienia
    const [openExamApprovalDialog, setOpenExamApprovalDialog] = useState(false); // Dialog zatwierdzenia egzaminu
    const user = useAppSelector(state => state.account.user);
    const isSuperAdmin = !!(user && user.roles?.includes('SuperAdmin'));
    const navigate = useNavigate();

    function getStatusIcon(currentState: number) {
        switch (currentState) {
            case 0:
                return <HourglassTopIcon />;
            case 1:
                return <HourglassTopIcon />;
            case 2:
                return <DoneIcon />;
            case 3:
                return <DoneIcon />;
            case 4:
                return <HelpOutlineIcon />;
            case 5:
                return <CancelIcon />;
            case 6:
                return <HourglassTopIcon />; // APPROVED_WITHOUT_SCANS
            case 7:
                return <DoneIcon />; // SCANS_ADDED_AFTER_APPROVAL
            default:
                return <ErrorIcon />;
        }
    }

    const downloadBlob = (blob: Blob, fileName: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    async function handleGenerateCertificates(orderId: number) {
        setIsGeneratingCertificate(true);
        try {
            const response = await agent.GenerateCertificate.generateCertificate(orderId);

            if (response.data instanceof Blob) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                downloadBlob(blob, `Certyfikaty_${orderId}.pdf`);
            } else {
                throw new Error('Nieprawidłowa odpowiedź z serwera');
            }
        } catch (error: any) {
            console.error('Błąd podczas generowania certyfikatów:', error);

            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Wystąpił błąd podczas generowania certyfikatów');
            }
        } finally {
            setIsGeneratingCertificate(false);
        }
    }

    useEffect(() => {
        if (!order) dispatch(fetchAdminOrderAsync(parseInt(id!)))
    }, [id, dispatch, order]);

    useEffect(() => {
        if (order) {
            setIsTestAvailable(order.isTestAvailable ? "Tak" : "Nie");
            setIsGraphical(order.isGraphical ? "Tak" : "Nie");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.isTestAvailable, order?.isGraphical]);

    // Funkcja do pobierania historii zamówienia
    const fetchHistory = useCallback(async () => {
        if (order?.publicId) {
            setLoading(true);
            try {
                const historyOfOrder: Order[] = await agent.Admin.Orders.getHistoryOfOrder(order.publicId);

                const timelineData: TimeLineElement[] = [];
                const statusTracker: { [key: number]: boolean } = {};

                historyOfOrder.forEach(h => {
                    const isFirstOccurrence = !statusTracker[h.orderState];
                    statusTracker[h.orderState] = true;

                    timelineData.push({
                        dateAndTimeToDisplay: new Date(h.updatingDateTime).toLocaleString(),
                        align: Align.Left,
                        header: h.updatingUserIsAdmin == true ? 'Administrator' : 'Klient',
                        subtitle: isFirstOccurrence ? getOrderStatusTitle(h.orderState) : 'Edytowano',
                        icon: getStatusIcon(h.orderState),
                        iconColor: getOrderStatusColor(h.orderState)
                    });
                });

                setTimeLineElements(timelineData);
            } catch (error) {
                console.error("Failed to fetch order history:", error);
            } finally {
                setLoading(false);
            }
        }
    }, [order?.publicId]);

    // Pobierz historię przy pierwszym renderze
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    // Pokaż loader tylko gdy zamówienie nie istnieje i trwa ładowanie
    // Nie pokazuj loadera przy odświeżaniu istniejącego zamówienia (np. po generowaniu AI)
    if (!order && orderStatus.includes('pending')) return <LoadingComponent />

    if (!order) return <NotFound />

    // Obsługa zatwierdzenia egzaminu (wywoływana z ExamApprovalConfirmDialog)
    async function handleConfirmExamApproval(examDateISO: string, sendEmail: boolean) {
        setLoading(true);
        try {
            if (order) {
                await agent.Admin.Orders.approveExam(order.publicId, examDateISO, sendEmail);
                if (sendEmail) {
                    toast.success('Zatwierdzono egzaminy. Wysłano powiadomienie e-mail do klienta.');
                } else {
                    toast.success('Zatwierdzono egzaminy (bez powiadomienia e-mail).');
                }
                dispatch(fetchAdminOrderAsync(parseInt(id!)));
                dispatch(fetchClientOrderAsync(parseInt(id!)));
            } else {
                toast.error('Wystąpił błąd');
            }
        } catch (error) {
            console.log(error);
            toast.error('Nie udało się zatwierdzić egzaminu.');
        } finally {
            setLoading(false);
            setOpenExamApprovalDialog(false);
        }
    }

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleDeleteOrder = async () => {
        setIsDeleting(true);
        try {
            await agent.Admin.Orders.delete(order.publicId);
            toast.success('Usunięto zamówienie');
            dispatch(fetchAdminOrdersAsync());
        } catch (error) {
            console.error('Wystąpił błąd podczas anulowania zamówienia:', error);
            toast.error('Nie udało się usunąć zamówienia.');
        } finally {
            setIsDeleting(false);
            handleCloseDeleteDialog();
            navigate("/admin/orders");
        }
    }

    const handleConfirmCancel = async () => {
        setIsCancelling(true);
        try {
            await agent.Admin.Orders.cancelOrder(order.publicId);
            toast.success('Zamówienie zostało anulowane.');
            dispatch(fetchAdminOrderAsync(parseInt(id!)));
        } catch (error) {
            console.error('Wystąpił błąd podczas anulowania zamówienia:', error);
            toast.error('Nie udało się anulować zamówienia.');
        } finally {
            setIsCancelling(false);
            handleCloseDialog();
        }
    };

    const handleRejectCancel = async () => {
        setIsCancelling(true);
        try {
            await agent.Admin.Orders.rejectCancellation(order.publicId);
            toast.success('Zapytanie o anulowanie zamówienia zostało odrzucone.');
            dispatch(fetchAdminOrderAsync(parseInt(id!)));
        } catch (error) {
            console.error('Wystąpił błąd podczas odrzucania anulowania zamówienia:', error);
            toast.error('Nie udało się odrzucić anulowania zamówienia.');
        } finally {
            setIsCancelling(false);
            handleCloseDialog();
        }
    };

    // Funkcja pobierania testu PDF (tylko dla questionsJson/AI)
    async function handleDownloadTestPdf() {
        setIsDownloadingTest(true);
        try {
            // Dla testów graficznych - pobierz przez API proxy zamiast generować
            if (order!.isGraphical && order!.packageTestPath) {
                await agent.Admin.Orders.downloadPackageTestFile(order!.publicId, 'test');
                toast.success('Test został pomyślnie pobrany');
            } else {
                // Dla testów AI - pobierz wygenerowany PDF
                if (!order || !order.questionsJson || order.questionsJson === "[]" || order.questionsJson === "{}") {
                    toast.error("Brak danych testu do pobrania");
                    return;
                }
                await agent.Admin.Orders.generateTestPdf(order.publicId);
                toast.success("Test PDF został pobrany");
            }
        } catch (error) {
            console.error("Błąd podczas pobierania testu PDF:", error);
            toast.error("Wystąpił błąd podczas pobierania testu PDF");
        } finally {
            setIsDownloadingTest(false);
        }
    }

    // Funkcja pobierania klucza odpowiedzi PDF (tylko dla questionsJson/AI)
    async function handleDownloadAnswerKeyPdf() {
        setIsDownloadingKey(true);
        try {
            // Dla testów graficznych - pobierz przez API proxy zamiast generować
            if (order!.isGraphical && order!.packageTestKeyPath) {
                await agent.Admin.Orders.downloadPackageTestFile(order!.publicId, 'key');
                toast.success('Klucz odpowiedzi został pomyślnie pobrany');
            } else {
                // Dla testów AI - pobierz wygenerowany PDF
                if (!order || !order.questionsJson || order.questionsJson === "[]" || order.questionsJson === "{}") {
                    toast.error("Brak danych klucza odpowiedzi do pobrania");
                    return;
                }
                await agent.Admin.Orders.generateAnswerKeyPdf(order.publicId);
                toast.success("Klucz odpowiedzi PDF został pobrany");
            }
        } catch (error) {
            console.error("Błąd podczas pobierania klucza odpowiedzi PDF:", error);
            toast.error("Wystąpił błąd podczas pobierania klucza odpowiedzi PDF");
        } finally {
            setIsDownloadingKey(false);
        }
    }

    // Funkcja pobierania testu graficznego (pliki z Cloudinary)
    async function handleDownloadGraphicalTest() {
        if (!order?.packageTestPath) {
            toast.error("Brak arkusza egzaminacyjnego do pobrania");
            return;
        }
        setIsDownloadingGraphicalTest(true);
        try {
            await agent.Admin.Orders.downloadPackageTestFile(order.publicId, 'test');
            toast.success('Arkusz egzaminacyjny został pomyślnie pobrany');
        } catch (error) {
            console.error("Błąd podczas pobierania arkusza egzaminacyjnego:", error);
            toast.error("Wystąpił błąd podczas pobierania arkusza egzaminacyjnego");
        } finally {
            setIsDownloadingGraphicalTest(false);
        }
    }

    // Funkcja pobierania klucza graficznego (pliki z Cloudinary)
    async function handleDownloadGraphicalKey() {
        if (!order?.packageTestKeyPath) {
            toast.error("Brak klucza odpowiedzi do pobrania");
            return;
        }
        setIsDownloadingGraphicalKey(true);
        try {
            await agent.Admin.Orders.downloadPackageTestFile(order.publicId, 'key');
            toast.success('Klucz odpowiedzi został pomyślnie pobrany');
        } catch (error) {
            console.error("Błąd podczas pobierania klucza odpowiedzi:", error);
            toast.error("Wystąpił błąd podczas pobierania klucza odpowiedzi");
        } finally {
            setIsDownloadingGraphicalKey(false);
        }
    }

    // Funkcja podglądu pliku w nowej karcie
    function handlePreviewFile(filePath: string) {
        if (filePath) {
            window.open(filePath, '_blank');
        }
    }

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Otwiera dialog potwierdzenia zmiany dostępności testu
    const handleChangeAvailability = (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value === "Tak";

        // Sprawdź, czy test faktycznie istnieje przed udostępnieniem
        if (newValue && !canEnableTestAvailability()) {
            toast.error("Nie można udostępnić testu. Brak wymaganych materiałów testowych.");
            return; // Przerwij funkcję
        }

        // Otwórz dialog potwierdzenia
        setPendingTestAvailability(newValue);
        setOpenTestAvailabilityDialog(true);
    };

    // Potwierdza zmianę dostępności testu (wywoływane z dialogu)
    const handleConfirmTestAvailability = async (sendEmail: boolean) => {
        const previousValue = isTestAvailable;
        const newValue = pendingTestAvailability;

        // Natychmiast aktualizuj UI dla lepszego UX
        setIsTestAvailable(newValue ? "Tak" : "Nie");
        setLoadingAvailability(true);

        try {
            // Wywołaj API z parametrem sendEmail
            const response = await agent.Admin.Orders.changeAvailabilityFlag(
                parseInt(id!, 10),
                { isTestAvailable: newValue, sendEmail }
            );

            // Zaktualizuj order w Redux store
            if (response) {
                dispatch(setAdminOrder(response));

                // Wyświetl odpowiednią informację
                if (newValue) {
                    if (sendEmail) {
                        toast.success("Test został udostępniony klientowi. Wysłano powiadomienie e-mail.");
                    } else {
                        toast.success("Test został udostępniony klientowi (bez powiadomienia e-mail).");
                    }
                } else {
                    toast.success("Test został ukryty przed klientem.");
                }
            }
        } catch (error) {
            // W przypadku błędu przywróć poprzednią wartość
            setIsTestAvailable(previousValue);
            toast.error("Nie udało się zmienić dostępności testu.");
            console.error(error);
        } finally {
            setLoadingAvailability(false);
            setOpenTestAvailabilityDialog(false);
        }
    };

    const handleChangeGraphical = (event: SelectChangeEvent<string>) => {
        setPendingGraphicalValue(event.target.value);
        setOpenGraphicalDialog(true);
    };

    const handleConfirmGraphicalChange = async () => {
        if (pendingGraphicalValue == null) return;
        const previousValue = isGraphical;
        const newValue = pendingGraphicalValue === "Tak";
        setIsGraphical(pendingGraphicalValue);
        setLoadingGraphical(true);
        setOpenGraphicalDialog(false);

        try {
            const response = await agent.Admin.Orders.changeGraphicalFlag(parseInt(id!, 10), { isGraphical: newValue });

            if (response) {
                toast.success("Zmieniono typ usługi.");
                dispatch(setAdminOrder(response));
                dispatch(setClientOrder(response));
            } else {
                throw new Error('Niewłaściwa odpowiedź serwera.');
            }
        } catch (error) {
            console.error('Wystąpił błąd podczas zmiany typu usługi:', error);
            toast.error('Nie udało się zmienić typu usługi.');
            setIsGraphical(previousValue);
        } finally {
            setLoadingGraphical(false);
            setPendingGraphicalValue(null);
        }
    };

    const handleCancelGraphicalChange = () => {
        setPendingGraphicalValue(null);
        setOpenGraphicalDialog(false);
    };

    const canEnableTestAvailability = () => {
        if (!order) return false;

        // Dla testów graficznych sprawdź packageTestPath
        if (order.isGraphical) {
            return !!(order.packageTestPath && order.packageTestPath !== '');
        }
        // Dla testów tekstowych sprawdź questionsJson
        else {
            // Sprawdź czy questionsJson istnieje i nie jest pustą tablicą/obiektem
            if (!order.questionsJson) return false;

            // Jeśli to string, sprawdź czy to nie pusty JSON
            if (typeof order.questionsJson === 'string') {
                const json = order.questionsJson.trim();
                return json !== '' && json !== '[]' && json !== '{}';
            }

            // Jeśli to tablica, sprawdź czy nie jest pusta
            if (Array.isArray(order.questionsJson)) {
                return (order.questionsJson as any[]).length > 0;
            }

            // W innych przypadkach sprawdź czy jest jakikolwiek obiekt
            return !!order.questionsJson;
        }
    };

    const renderAvailableTestData = () => {
        if (!order) return null;

        const hasGraphicalTestData = order.packageTestPath && order.packageTestPath !== '';
        const hasTextTestData = hasValidQuestionsJson();
        const isTestAvailableFlag = order.isTestAvailable;

        // Funkcja pomocnicza do sprawdzania questionsJson
        function hasValidQuestionsJson() {
            if (!order?.questionsJson) return false;

            if (typeof order.questionsJson === 'string') {
                const json = order.questionsJson.trim();
                return json !== '' && json !== '[]' && json !== '{}';
            }

            if (Array.isArray(order.questionsJson)) {
                return (order.questionsJson as any[]).length > 0;
            }

            return !!order.questionsJson;
        }

        // Ostrzeżenie, gdy test jest udostępniony, ale brak materiałów testowych
        if (isTestAvailableFlag && !canEnableTestAvailability()) {
            return (
                <Alert
                    severity="warning"
                    icon={<InfoIcon />}
                    sx={{ my: 2, mr: 2 }}
                >
                    <Typography variant="subtitle1" fontWeight="bold">
                        UWAGA: Test jest udostępniony, ale brak materiałów testowych!
                    </Typography>
                    <Typography variant="body2">
                        To zamówienie ma włączoną flagę dostępności testu, ale {order.isGraphical
                            ? "nie posiada przypisanego arkusza egzaminacyjnego"
                            : "nie posiada przypisanych pytań testowych"}.
                        Klient nie będzie mógł pobrać testu pomimo jego udostępnienia.
                    </Typography>
                </Alert>
            );
        }

        // Informacja o dostępności dwóch typów testów
        if (hasGraphicalTestData && hasTextTestData) {
            return (
                <Alert severity="info" sx={{ my: 2, mr: 2 }}>
                    To zamówienie posiada dane dla obu typów testów.
                    Obecnie aktywny jest test {order.isGraphical ? 'graficzny' : 'tekstowy'}.
                </Alert>
            );
        }

        return null;
    };

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn title={"Zamówienie #" + order.publicId} backPath="/admin/orders" />

            {/* Informacja o weryfikacji zamówienia - tylko dla Admin (nie SuperAdmin) */}
            {(isAIGenerating || order.isTestGenerationInProgress) && !isSuperAdmin && (
                <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                        Zamówienie jest weryfikowane
                    </Typography>
                    <Typography variant="caption">
                        Proces potrwa około minuty. Strona odświeży się automatycznie. Po tym czasie będziesz mógł zarządzać zamówieniem.
                    </Typography>
                    <LinearProgress sx={{ mt: 1 }} />
                </Alert>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%', mb: 2 }}>
                <Box display="flex" alignItems="center">
                    <FormControl variant="outlined" sx={{ minWidth: 140, mr: 2 }}>
                        <InputLabel>Udostępnienie testu</InputLabel>
                        <Select
                            value={isTestAvailable}
                            onChange={handleChangeAvailability}
                            label="Udostępnienie testu"
                            disabled={loadingAvailability || isAIGenerating || (isTestAvailable === "Nie" && !canEnableTestAvailability())}
                        >
                            <MenuItem value="Tak" disabled={!canEnableTestAvailability()}>Tak</MenuItem>
                            <MenuItem value="Nie">Nie</MenuItem>
                        </Select>
                        {loadingAvailability && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', right: '50%', marginTop: '-12px', marginRight: '-12px' }} />}
                    </FormControl>

                    <FormControl variant="outlined" sx={{ minWidth: 140 }}>
                        <InputLabel>Rodzaj testu</InputLabel>
                        <Select
                            value={isGraphical}
                            onChange={handleChangeGraphical}
                            label="Typ usługi"
                            disabled={loadingGraphical || isAIGenerating}
                        >
                            <MenuItem value="Tak">Graficzny</MenuItem>
                            <MenuItem value="Nie">Tekstowy</MenuItem>
                        </Select>
                        {loadingGraphical && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', right: '50%', marginTop: '-12px', marginRight: '-12px' }} />}
                    </FormControl>

                    <Dialog
                        open={openGraphicalDialog}
                        onClose={handleCancelGraphicalChange}
                        aria-labelledby="confirm-graphical-change-title"
                        aria-describedby="confirm-graphical-change-description"
                    >
                        <DialogTitle id="confirm-graphical-change-title">Potwierdź zmianę rodzaju testu</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="confirm-graphical-change-description">
                                Czy na pewno chcesz zmienić rodzaj testu na <b>{pendingGraphicalValue === "Tak" ? "Graficzny" : "Tekstowy"}</b>?<br/>
                                Ta operacja może wpłynąć na dostępność materiałów testowych.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCancelGraphicalChange} color="inherit">Anuluj</Button>
                            <Button onClick={handleConfirmGraphicalChange} color="primary" autoFocus>Potwierdź</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Dodaj przyciski dla przesłania plików */}
                    {isGraphical === "Tak" && (
                        <>
                            <AdminTestFileUploader order={order} fileType="test" />
                            <AdminTestFileUploader order={order} fileType="key" />
                        </>
                    )}

                    <AdminTestQuestionsEditor
                        order={order}
                        disabled={isGraphical === "Tak" || loadingGraphical || isAIGenerating}
                    />
                </Box>
                {isSuperAdmin ? (
                    <Button
                        onClick={handleOpenDeleteDialog}
                        color="error"
                        variant='contained'
                        size='large'
                        disabled={isAIGenerating}
                        sx={{}}
                    >
                        Usuń zamówienie
                    </Button>
                ) : ''
                }
                <Button
                    onClick={handleOpenDialog}
                    color="error"
                    variant='outlined'
                    size='large'
                    disabled={order.orderState == 5 || isAIGenerating}
                    sx={{ mr: { xs: 0, md: 2 } }}
                >
                    {order.orderState == 4 ? 'Rozwiąż prośbę' : 'Anuluj zamówienie'}
                </Button>
            </Box>

            {renderAvailableTestData()}

            <AdminTestAvailabilityAlerts
                order={order}
                onGeneratingChange={setIsAIGenerating}
                onGenerationComplete={fetchHistory}
            />

            <Grid container>
                <Grid item md={7} sm={12}>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Podstawowe informacje'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.customeClientServiceName} variant="h5" />
                                    <Typography variant="overline">
                                        nazwa wskazana przez Klienta
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.serviceName} variant="h5" />
                                    <Typography variant="overline">
                                        bazowa nazwa
                                    </Typography>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <Typography variant="h5">
                                        {formatToLocalDate(order.orderDateTime.toString())} {formatToLocalTime(order.orderDateTime.toString())}
                                    </Typography>
                                    <Typography variant="overline">
                                        data i godzina złożenia zamówienia
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {order.preposedDateTime
                                            ? formatToLocalDate(order.preposedDateTime.toString())
                                            : 'Brak proponowanej daty'}
                                    </Typography>
                                    <Typography variant="overline">
                                        proponowana data realizacji egzaminu
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.trainingProgram} variant="h6" />
                                    <Typography variant="overline">
                                        wskazany program szkolenia (jeżeli dotyczy)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.clientComment} variant="h6" />
                                    <Typography variant="overline">
                                        komentarz Klienta
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Cena'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {currencyFormat(order.totalDiscountedPrice)}
                                    </Typography>
                                    <Typography variant="overline">
                                        wartość zamówienia
                                    </Typography>
                                    <Typography variant="h5">
                                        {currencyFormat(order.unitDiscountedPrice)}
                                    </Typography>
                                    <Typography variant="overline">
                                        cena jedostkowa
                                    </Typography>
                                    <Typography variant="h5">
                                        {order.totalItem}
                                    </Typography>
                                    <Typography variant="overline">
                                        liczba osób
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Dane klienta'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.userDisplayName} variant="h5" />
                                    <Typography variant="overline">
                                        nazwa Klienta
                                    </Typography>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <Typography variant="h6" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                        {order.userEmail}
                                    </Typography>
                                    <Typography variant="overline">
                                        email
                                    </Typography>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <Typography variant="h5" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                        {order.userPhone}
                                    </Typography>
                                    <Typography variant="overline">
                                        telefon kontaktowy
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Dane Płatnika'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.payerClientName} variant="h5" />
                                    <Typography variant="overline">
                                        nazwa Płatnika
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <AppExpandableText text={order.payerClientAddress} variant="h6" />
                                    <Typography variant="overline">
                                        adres Płatnika
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                        {order.payerClientNIP ? order.payerClientNIP : order.payerNIP}
                                    </Typography>
                                    <Typography variant="overline">
                                        NIP Płatnika
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={12} md={5} >
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Historia'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <Grid container>
                            <Grid item xs={12}>

                            </Grid>
                            <Grid item xs={12}>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                        <CircularProgress />
                                    </Box>
                                ) : <AppTimeline elements={timeLineElements} />}
                            </Grid>
                        </Grid>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mr: { xs: 0, md: 2 }, mb: 2 }}>
                        <CardHeader title='Korespondencja'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <Grid container>
                            <Grid item xs={12}>

                            </Grid>
                            <Grid item xs={12}>
                                <OrderHelpInOrderChat orderId={parseInt(id!)} />
                            </Grid>
                        </Grid>
                    </Card>

                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Pobierz arkusze egzaminacyjne'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            {/* Sprawdzamy czy istnieją jakiekolwiek materiały testowe */}
                            {(order.packageTestPath || order.packageTestKeyPath || (order.questionsJson && order.questionsJson !== "[]" && order.questionsJson !== "{}")) ? (
                                <Grid container spacing={2}>
                                    {/* Sekcja plików graficznych (z Cloudinary) - jeśli istnieją */}
                                    {(order.packageTestPath || order.packageTestKeyPath) && (
                                        <Grid item xs={12}>
                                            {order?.isTestAvailable && order.isGraphical && (
                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        Klient widzi poniższy egzamin (wersja graficzna - skan/pdf/obraz):
                                                    </Typography>
                                                </Alert>
                                            )}
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Pliki przesłane (graficzne/skany):
                                            </Typography>
                                            {order.packageTestPath && (
                                                <>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                        <LoadingButton
                                                            loading={isDownloadingGraphicalTest}
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleDownloadGraphicalTest}
                                                            startIcon={<i className="fa fa-download" />}
                                                            disabled={isAIGenerating}
                                                            size="small"
                                                        >
                                                            Pobierz
                                                        </LoadingButton>
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => handlePreviewFile(order.packageTestPath!)}
                                                            startIcon={<i className="fa fa-eye" />}
                                                            disabled={isAIGenerating}
                                                            size="small"
                                                        >
                                                            Podgląd
                                                        </Button>
                                                    </Box>
                                                    <Typography variant="overline">
                                                        arkusz egzaminacyjny (plik)
                                                    </Typography>
                                                </>
                                            )}

                                            {order.packageTestPath && order.packageTestKeyPath && (
                                                <Divider sx={{ mt: 1, mb: 2 }} />
                                            )}

                                            {order.packageTestKeyPath && (
                                                <>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                        <LoadingButton
                                                            loading={isDownloadingGraphicalKey}
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={handleDownloadGraphicalKey}
                                                            startIcon={<i className="fa fa-download" />}
                                                            disabled={isAIGenerating}
                                                            size="small"
                                                        >
                                                            Pobierz
                                                        </LoadingButton>
                                                        <Button
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() => handlePreviewFile(order.packageTestKeyPath!)}
                                                            startIcon={<i className="fa fa-eye" />}
                                                            disabled={isAIGenerating}
                                                            size="small"
                                                        >
                                                            Podgląd
                                                        </Button>
                                                    </Box>
                                                    <Typography variant="overline">
                                                        klucz odpowiedzi (plik)
                                                    </Typography>
                                                </>
                                            )}
                                        </Grid>
                                    )}

                                    {/* Divider między sekcjami jeśli obie istnieją */}
                                    {(order.packageTestPath || order.packageTestKeyPath) && 
                                     order.questionsJson && order.questionsJson !== "[]" && order.questionsJson !== "{}" && (
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 2 }} />
                                        </Grid>
                                    )}

                                    {/* Sekcja testów generowanych z questionsJson - jeśli istnieją */}
                                    {order.questionsJson && order.questionsJson !== "[]" && order.questionsJson !== "{}" && (
                                        <Grid item xs={12}>
                                            {order?.isTestAvailable && !order.isGraphical && (
                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        Klient widzi poniższy rodzaj egzaminu (PDF):
                                                    </Typography>
                                                </Alert>
                                            )}
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Test wygenerowany z pytań (PDF):
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                                                <LoadingButton
                                                    loading={isDownloadingTest}
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleDownloadTestPdf}
                                                    startIcon={<i className="fa fa-file-pdf-o" />}
                                                    disabled={isAIGenerating}
                                                >
                                                    Pobierz test PDF
                                                </LoadingButton>
                                                <LoadingButton
                                                    loading={isDownloadingKey}
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={handleDownloadAnswerKeyPdf}
                                                    startIcon={<i className="fa fa-key" />}
                                                    disabled={isAIGenerating}
                                                >
                                                    Pobierz klucz odpowiedzi
                                                </LoadingButton>
                                            </Box>
                                            <Typography variant="overline" display="block">
                                                Arkusz egzaminacyjny i klucz odpowiedzi
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            ) : (
                                <Alert severity="warning">
                                    Brak arkuszy egzaminacyjnych dla tego zamówienia. Aby udostępnić materiały testowe, dodaj pliki testów lub pytania.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {/* Karta widoczna gdy są skany LUB gdy SuperAdmin może zatwierdzić bez skanów LUB w stanach 6/7 */}
                    {((order.solvedTestsPaths != null && order.solvedTestsPaths != '') || 
                      (isSuperAdmin && order.benefitients && order.orderState === OrderState.ORDER_CREATED) ||
                      order.orderState === OrderState.APPROVED_WITHOUT_SCANS ||
                      order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL) ?
                        (
                            <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                                <CollapsibleParticipantsList benefitients={order.benefitients} />
                                <Divider />
                                <CardContent>
                                    {/* Alert informacyjny - tylko jeden w zależności od stanu */}
                                    {order.orderState === OrderState.APPROVED_WITHOUT_SCANS ? (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Egzamin został zatwierdzony bez skanów. Oczekiwanie na przesłanie skanów przez klienta.
                                        </Alert>
                                    ) : order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL ? (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            Klient przesłał skany. Możesz teraz zatwierdzić egzamin.
                                        </Alert>
                                    ) : order.isExamApproved ? (
                                        <Alert severity="info" sx={{ mb: 1 }}>Egzaminy zostały już zatwierdzone</Alert>
                                    ) : isSuperAdmin && (!order.solvedTestsPaths || order.solvedTestsPaths === '') && order.orderState === OrderState.ORDER_CREATED ? (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            <strong>Uwaga:</strong> Brak przesłanych skanów egzaminacyjnych. 
                                            Jako SuperAdmin możesz zatwierdzić egzamin bez skanów. 
                                            Klient otrzyma powiadomienie o konieczności dosłania skanów.
                                        </Alert>
                                    ) : null}
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <LoadingButton
                                            loading={loading}
                                            onClick={() => setOpenExamApprovalDialog(true)}
                                            disabled={isAIGenerating || !(
                                                order.isExamApproved || // Admin/SuperAdmin zawsze może edytować datę po zatwierdzeniu
                                                order.orderState === OrderState.SCANS_ACCEPTED || 
                                                order.orderState === OrderState.SCANS_ADDED ||
                                                order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL ||
                                                (isSuperAdmin && order.orderState === OrderState.ORDER_CREATED && order.benefitients)
                                            )}
                                            variant='outlined'
                                            color={isSuperAdmin && (!order.solvedTestsPaths || order.solvedTestsPaths === '') && order.orderState === OrderState.ORDER_CREATED ? 'warning' : 'primary'}
                                        >
                                            {order.isExamApproved 
                                                ? 'Edytuj datę egzaminów' 
                                                : (isSuperAdmin && (!order.solvedTestsPaths || order.solvedTestsPaths === '') && order.orderState === OrderState.ORDER_CREATED
                                                    ? 'Zatwierdź egzaminy (bez skanów)' 
                                                    : 'Zatwierdź egzaminy uczestników')}
                                        </LoadingButton>
                                        {(order.orderState === OrderState.SCANS_ACCEPTED || 
                                          order.orderState === OrderState.SCANS_ADDED ||
                                          order.orderState === OrderState.APPROVED_WITHOUT_SCANS ||
                                          order.orderState === OrderState.SCANS_ADDED_AFTER_APPROVAL) && (
                                            <Tooltip title={!order.isExamApproved ? 'Testy nie zostały jeszcze zatwierdzone' : ''}>
                                                <span>
                                                    <LoadingButton
                                                        loading={isGeneratingCertificate}
                                                        disabled={!order.isExamApproved || isAIGenerating}
                                                        onClick={() => handleGenerateCertificates(order.publicId)}
                                                        variant='outlined'
                                                        color='success'
                                                    >
                                                        Pobierz certyfikaty
                                                    </LoadingButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        ) : ''}

                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Przesłane skany'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <DownloadAndPreviewButtons order={{
                                path: order.solvedTestsPaths
                            }} count={true} disabled={isAIGenerating} />
                        </CardContent>
                        <Divider />
                        <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                sx={{ height: '40px' }}
                                variant='outlined'
                                onClick={handleOpen}
                                size="small"
                                title='Załącz'
                                color='success'
                                disabled={isAIGenerating}
                            >
                                Załącz
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Dialog open={open} onClose={handleClose}>
                <DialogContent>
                    <AdminOrderScanUploadForm order={order} cancelUpload={handleClose} />
                </DialogContent>
            </Dialog>

            {/* Dialog potwierdzenia udostępnienia/ukrycia testu */}
            <TestAvailabilityConfirmDialog
                open={openTestAvailabilityDialog}
                onClose={() => setOpenTestAvailabilityDialog(false)}
                onConfirm={handleConfirmTestAvailability}
                isEnabling={pendingTestAvailability}
                serviceName={order.serviceName || ''}
                userEmail={order.userEmail || ''}
            />

            {/* Dialog potwierdzenia zatwierdzenia egzaminu */}
            <ExamApprovalConfirmDialog
                open={openExamApprovalDialog}
                onClose={() => setOpenExamApprovalDialog(false)}
                onConfirm={handleConfirmExamApproval}
                serviceName={order.serviceName || ''}
                userEmail={order.userEmail || ''}
                hasScans={!!(order.solvedTestsPaths && order.solvedTestsPaths !== '')}
                defaultDate={order.preposedDateTime ? new Date(order.preposedDateTime) : null}
            />

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Box width='100%' display="flex" justifyContent='center'>
                        {"Anulowanie zamówienia"}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {order.orderState == 4 ? 'Klient rozpoczął proces zapytania o anulowanie zamówienia, czy potwierdzasz anulowanie zamówienia?'
                            : order.isPaid ? 'Czy na pewno chcesz anulować opłacone zamówienie?' : 'Czy chcesz anulować zamówienie?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Box width='100%' display="flex" justifyContent='space-between'>
                        {order.orderState !== 4 ? (
                            <>
                                <Button onClick={handleCloseDialog} variant='outlined' sx={{ ml: 2 }}>Nie</Button>
                                <LoadingButton
                                    onClick={handleConfirmCancel}
                                    autoFocus
                                    variant='outlined'
                                    color='error'
                                    loading={isCancelling}
                                    sx={{ mr: 2 }}
                                >
                                    Tak
                                </LoadingButton>
                            </>
                        ) : (
                            <>
                                <LoadingButton
                                    onClick={handleRejectCancel}
                                    variant='outlined'
                                    color="success"
                                    loading={isRejecting}
                                    sx={{ ml: 2 }}
                                >
                                    Odrzucam zapytanie
                                </LoadingButton>

                                <LoadingButton
                                    onClick={handleConfirmCancel}
                                    variant='outlined'
                                    color='error'
                                    loading={isCancelling}
                                    sx={{ mr: 2 }}
                                >
                                    Anuluję zamówienie
                                </LoadingButton>
                            </>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Box width='100%' display="flex" textAlign='center' justifyContent='center'>
                        {"Usuwanie zamówienia #" + order.publicId}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box width='100%' display="flex" flexDirection="column" justifyContent="center">
                        <DialogContentText id="alert-dialog-description" textAlign="center">
                            Czy chesz usunąć zamówienie o numerze #{order.publicId}?
                        </DialogContentText>
                        <Alert severity="error" sx={{ mt: 2, p: 1, width: '100%', textAlign: 'center' }}>
                            Usunięcie zamówienia jest nieodwracalne!
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box width='100%' display="flex" justifyContent='space-between'>
                        <Button onClick={handleCloseDeleteDialog} variant='outlined' sx={{ ml: 2 }}>Nie</Button>
                        <LoadingButton
                            onClick={handleDeleteOrder}
                            autoFocus
                            variant='contained'
                            color='error'
                            loading={isDeleting}
                            sx={{ mr: 2 }}
                        >
                            Tak
                        </LoadingButton>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    )
}
